import { captureException } from '@sentry/react'
import type { Extras } from '@sentry/types/build/types/extra'
import { Data, getBodyContentType, Method, toBody, toExtraJson, toFormData } from 'util/api'
import { omit, omitUndefined } from 'util/object'

async function extractErrorJson(response: Response): Promise<Record<string, unknown>> {
  try {
    return await response.json()
  } catch {
    return {}
  }
}

export class HttpError extends Error {
  readonly status: number
  readonly json: Record<string, unknown>

  static fromResponse(response: Response, json: Record<string, unknown>): HttpError {
    const { status, statusText } = response
    const message = `http error ${status} ${statusText}`
    return new HttpError(status, message, json)
  }

  constructor(status: number, message: string, json: Record<string, unknown>) {
    super(message)
    this.status = status
    this.json = json
  }
}

export type ExtractResponse<T> = (response: Response) => Promise<T>
export type Tune<T = unknown> = {
  extend?: false
  extract?: ExtractResponse<T>
}

class Rest {
  client?: string
  company?: string
  access?: string
  origin?: string

  setup(client: string, company: string, access: string, origin: string) {
    rest.client = client
    rest.company = company
    rest.access = access
    rest.origin = origin
  }

  get<T>(path: string, data?: Data, tune?: Tune<T>): Promise<T> {
    data = this.extend(data, tune)

    const split = path.split('?')
    const base = split[0]
    const params = new URLSearchParams(split[1])

    if (data) {
      for (const key of Object.keys(data)) {
        const value = data[key]
        if (value != null) params.append(key, value.toString())
      }
    }

    const search = params.size === 0 ? '' : '?' + params.toString()
    return this.fetch('GET', base + search, {}, tune)
  }

  post<T>(path: string, data?: Data, tune?: Tune<T>): Promise<T> {
    data = this.extend(data, tune)

    const body = toBody(data)
    const headers = omit<Record<string, string>>({
      'Content-Type': getBodyContentType(body),
    })
    return this.fetch('POST', path, { body, headers }, tune)
  }

  form<T>(path: string, data: Data, tune?: Tune<T>): Promise<T> {
    data = this.extend(data, tune)

    const body = toFormData(data)
    return this.fetch('POST', path, { body }, tune)
  }

  private extend(data?: Data, tune?: Tune): Data {
    data = data ?? {}
    if (tune?.extend === false) return omitUndefined(data)

    data['client'] = this.client || undefined
    data['company'] = this.company || undefined
    data['access'] = this.access || undefined
    data['origin'] = this.origin || undefined
    return omitUndefined(data)
  }

  private async fetch<T>(method: Method, path: string, options: RequestInit, tune?: Tune<T>): Promise<T> {
    try {
      const response = await fetch(path, { method, ...options })
      if (!response.ok) throw HttpError.fromResponse(response, await extractErrorJson(response))
      if (tune?.extract) return await tune.extract(response)
      return await response.json()
    } catch (e) {
      const extra = this.extra(method, path, options)
      captureException(e, { extra })
      throw e
    }
  }

  private extra(method: Method, fullPath: string, options: RequestInit | undefined): Extras {
    const url = new URL(fullPath, location.href)
    const path = url.pathname
    const query = Object.fromEntries(url.searchParams.entries())
    const body = toExtraJson(options?.body)
    return { request: { method, path, body, query } }
  }
}

export const rest = new Rest()
